import type { AppError, AppErrorSource } from '@/types'

/** Central AppError factory. Never leaks secrets or raw SQL to userMessage. */
export function makeAppError(input: AppError): AppError {
  return input
}

export function newCorrelationId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `c_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

type ProviderErrorLike = {
  message?: string
  code?: string | number
  status?: number
  details?: string
}

/**
 * Maps Supabase/PostgREST/Auth/Storage errors to safe AppError values.
 * Raw provider messages go to technicalMessage only, never to userMessage.
 */
export function mapProviderError(
  error: unknown,
  source: AppErrorSource,
  fallbackUserMessage = 'Er ging iets mis. Probeer het opnieuw.',
): AppError {
  const correlationId = newCorrelationId()
  const e = (error ?? {}) as ProviderErrorLike
  const technicalMessage = [e.message, e.code ? `code=${e.code}` : '']
    .filter(Boolean)
    .join(' | ')
    .slice(0, 500)

  const msg = (e.message ?? '').toLowerCase()

  if (msg.includes('failed to fetch') || msg.includes('network') || msg.includes('load failed')) {
    return makeAppError({
      code: 'NETWORK_UNREACHABLE',
      userMessage: 'Geen verbinding. Controleer je internet en probeer opnieuw.',
      technicalMessage,
      retryable: true,
      source: 'network',
      correlationId,
    })
  }
  if (e.status === 401 || msg.includes('jwt') || msg.includes('invalid login')) {
    return makeAppError({
      code: 'AUTH_SESSION_INVALID',
      userMessage: 'Je sessie is verlopen. Log opnieuw in.',
      technicalMessage,
      retryable: false,
      source: 'auth',
      correlationId,
    })
  }
  if (e.status === 403 || msg.includes('row-level security') || msg.includes('permission denied')) {
    return makeAppError({
      code: 'PERMISSION_DENIED',
      userMessage: 'Je hebt geen toegang tot deze gegevens.',
      technicalMessage,
      retryable: false,
      source: 'database',
      correlationId,
    })
  }
  if (e.status === 429 || msg.includes('rate limit')) {
    return makeAppError({
      code: 'RATE_LIMITED',
      userMessage: 'Te veel pogingen. Wacht even en probeer opnieuw.',
      technicalMessage,
      retryable: true,
      source,
      correlationId,
    })
  }
  if (msg.includes('payload too large') || msg.includes('exceeded the maximum')) {
    return makeAppError({
      code: 'STORAGE_FILE_TOO_LARGE',
      userMessage: 'Het bestand is te groot. Kies een kleiner bestand.',
      technicalMessage,
      retryable: false,
      source: 'storage',
      correlationId,
    })
  }
  return makeAppError({
    code: 'UNKNOWN',
    userMessage: fallbackUserMessage,
    technicalMessage,
    retryable: true,
    source,
    correlationId,
  })
}

/** Type guard so UI can branch on typed errors. */
export function isAppError(error: unknown): error is AppError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'userMessage' in error &&
    'source' in error
  )
}

/** Always returns an AppError, converting unknown throws. */
export function toAppError(error: unknown, source: AppErrorSource): AppError {
  if (isAppError(error)) return error
  return mapProviderError(error, source)
}
